import { useMemo, useRef } from 'react';
import { useViewport } from '@xyflow/react';
import type { DetailLevel } from './FloatingNode';

/**
 * Zoom thresholds for detail level transitions.
 * Exported for testing purposes.
 *
 * The 4-level system provides progressive disclosure:
 * - icon: Overview mode - scan large workflows quickly
 * - navigation: Find nodes - badges help identify node types
 * - compact: Working mode - labels visible for editing
 * - detailed: Focus mode - all info visible for configuration
 */
export const ZOOM_THRESHOLDS = {
  /** Zoom level below which nodes show icon-only mode */
  ICON_MAX: 0.4,
  /** Zoom level below which nodes show navigation mode (icon + badges) */
  NAVIGATION_MAX: 0.7,
  /** Zoom level below which nodes show compact mode */
  COMPACT_MAX: 1.0,
  /** Hysteresis buffer to prevent flickering at threshold boundaries */
  HYSTERESIS: 0.05,
} as const;

/**
 * Hook that returns the current detail level based on zoom level.
 *
 * Uses hysteresis to prevent flickering when zoom hovers near thresholds.
 * For example, if transitioning from icon to navigation at 0.4, the reverse
 * transition requires zoom to drop below 0.35 (0.4 - 0.05 hysteresis).
 *
 * Detail levels (progressive disclosure):
 * - 'icon': zoom < 0.4 - Just icons, labels in tooltips (overview)
 * - 'navigation': 0.4 <= zoom < 0.7 - Icons + info badges (find nodes)
 * - 'compact': 0.7 <= zoom < 1.0 - Icons + labels + badges (working)
 * - 'detailed': zoom >= 1.0 - Full details with sub-labels (focus)
 *
 * @returns The current detail level
 */
export function useDetailLevel(): DetailLevel {
  const viewport = useViewport();
  const previousLevelRef = useRef<DetailLevel>('compact');

  return useMemo(() => {
    const zoom = viewport.zoom;
    const previousLevel = previousLevelRef.current;
    const { ICON_MAX, NAVIGATION_MAX, COMPACT_MAX, HYSTERESIS } = ZOOM_THRESHOLDS;

    let newLevel: DetailLevel;

    // Determine target level based on zoom thresholds
    const getTargetLevel = (z: number): DetailLevel => {
      if (z < ICON_MAX) return 'icon';
      if (z < NAVIGATION_MAX) return 'navigation';
      if (z < COMPACT_MAX) return 'compact';
      return 'detailed';
    };

    // Get level order for hysteresis calculation
    const levelOrder: DetailLevel[] = ['icon', 'navigation', 'compact', 'detailed'];
    const currentIndex = levelOrder.indexOf(previousLevel);
    const targetLevel = getTargetLevel(zoom);
    const targetIndex = levelOrder.indexOf(targetLevel);

    // Apply hysteresis: require crossing threshold + buffer when changing levels
    if (targetIndex > currentIndex) {
      // Zooming in: require exceeding threshold + hysteresis
      const threshold = targetIndex === 3 ? COMPACT_MAX :
                       targetIndex === 2 ? NAVIGATION_MAX :
                       ICON_MAX;
      newLevel = zoom >= threshold + HYSTERESIS ? targetLevel : previousLevel;
    } else if (targetIndex < currentIndex) {
      // Zooming out: require dropping below threshold - hysteresis
      const threshold = currentIndex === 3 ? COMPACT_MAX :
                       currentIndex === 2 ? NAVIGATION_MAX :
                       ICON_MAX;
      newLevel = zoom < threshold - HYSTERESIS ? targetLevel : previousLevel;
    } else {
      newLevel = targetLevel;
    }

    previousLevelRef.current = newLevel;
    return newLevel;
  }, [viewport.zoom]);
}
